import { z } from 'zod';
import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';

import { createBasicStoreState } from './basic-store';

export enum UIKey {
  BEFORE_PAYMENT_CREDITS = 'before_payment_credits',
  HAS_REMOVED_ORG = 'has_removed_org',
  SUBSCRIPTION_UPDATE = 'subscription_update',
  UTM_SOURCE = 'utm_source',
  UTM_MEDIUM = 'utm_medium',
  UTM_CAMPAIGN = 'utm_campaign',
  UTM_TERM = 'utm_term',
  GCLID = 'gclid',
  FBCLID = 'fbclid',
  LI_FAT_ID = 'li_fat_id',
  IS_ORG_LOADING = 'is_org_loading',
  SESSION_INITIALIZED = 'session_initialized',
  NOTIFICATION_DISMISSED = 'notification_dismissed',
  IS_TOP_BANNER_ACTIVE = 'is_top_banner_active',
}

type UIValueMap = {
  [UIKey.BEFORE_PAYMENT_CREDITS]: number;
  [UIKey.HAS_REMOVED_ORG]: boolean;
  [UIKey.SUBSCRIPTION_UPDATE]: string;
  [UIKey.UTM_SOURCE]: string;
  [UIKey.UTM_MEDIUM]: string;
  [UIKey.UTM_CAMPAIGN]: string;
  [UIKey.UTM_TERM]: string;
  [UIKey.GCLID]: string;
  [UIKey.FBCLID]: string;
  [UIKey.LI_FAT_ID]: string;
  [UIKey.IS_ORG_LOADING]: boolean;
  [UIKey.SESSION_INITIALIZED]: boolean;
  [UIKey.NOTIFICATION_DISMISSED]: boolean;
  [UIKey.IS_TOP_BANNER_ACTIVE]: boolean;
};

export type UIKv = {
  key: UIKey;
  value: UIValueMap[UIKey] | undefined;
  updatedAt: number;
};

type TModel = {
  // _id and key has the same value
  _id: string;
  key: string;
  value: any;
  // date
  updatedAt: number;
};

type StoreActions = {
  resetToStore: () => void;
  setToStore: (key: UIKv['key'], value: UIKv['value']) => void;
  getFromStore: <K extends UIKey>(key: K) => UIValueMap[typeof key] | undefined;
  clearUtmValues: () => void;
};

type FullStoreState = ReturnType<typeof createBasicStoreState<TModel>> & StoreActions;

// UTM and click ID keys that should be persisted to sessionStorage
const UTM_KEYS = [
  UIKey.UTM_SOURCE,
  UIKey.UTM_MEDIUM,
  UIKey.UTM_CAMPAIGN,
  UIKey.UTM_TERM,
  UIKey.GCLID,
  UIKey.FBCLID,
  UIKey.LI_FAT_ID,
] as const;

export const useStore = create<FullStoreState>()(
  devtools(
    persist(
      function (set, get) {
        const store: FullStoreState = {
          ...createBasicStoreState<TModel>(set, get),

          // Sync methods
          resetToStore: function () {
            // Preserve UTM values when resetting - they should persist through signup
            const currentRecord = get().record;
            const utmRecords: typeof currentRecord = {};

            // Save UTM values before clearing
            for (const utmKey of UTM_KEYS) {
              const utmRecord = currentRecord[utmKey];
              if (utmRecord != null) {
                utmRecords[utmKey] = utmRecord;
              }
            }

            // Clear everything
            get().clearStore();

            // Restore UTM values after clearing
            if (Object.keys(utmRecords).length > 0) {
              set(function (state) {
                return {
                  record: {
                    ...state.record,
                    ...utmRecords,
                  },
                };
              });
            }
          },

          setToStore: function (key, value) {
            const currentKv = get().getByIdFromStore(key)?.data;
            if (currentKv == null) {
              return get().updateToStore(key, {
                isLoading: false,
                hasError: undefined,
                data: {
                  key,
                  value,
                  updatedAt: Date.now(),
                  _id: key,
                },
              });
            }

            get().updateToStore(key, {
              isLoading: false,
              hasError: undefined,
              data: {
                ...currentKv,
                value,
                updatedAt: Date.now(),
              },
            });
          },

          getFromStore: function (key) {
            const uiState = get().getByIdFromStore(key)?.data;
            if (uiState == null) {
              return undefined;
            }

            switch (key) {
              case UIKey.BEFORE_PAYMENT_CREDITS:
                return z.number().safeParse(uiState.value).data as UIValueMap[typeof key];
              case UIKey.HAS_REMOVED_ORG:
                return z.boolean().safeParse(uiState.value).data as UIValueMap[typeof key];
              case UIKey.SUBSCRIPTION_UPDATE:
                return z.string().safeParse(uiState.value).data as UIValueMap[typeof key];
              case UIKey.UTM_SOURCE:
                return z.string().safeParse(uiState.value).data as UIValueMap[typeof key];
              case UIKey.UTM_MEDIUM:
                return z.string().safeParse(uiState.value).data as UIValueMap[typeof key];
              case UIKey.UTM_CAMPAIGN:
                return z.string().safeParse(uiState.value).data as UIValueMap[typeof key];
              case UIKey.UTM_TERM:
                return z.string().safeParse(uiState.value).data as UIValueMap[typeof key];
              case UIKey.GCLID:
                return z.string().safeParse(uiState.value).data as UIValueMap[typeof key];
              case UIKey.FBCLID:
                return z.string().safeParse(uiState.value).data as UIValueMap[typeof key];
              case UIKey.LI_FAT_ID:
                return z.string().safeParse(uiState.value).data as UIValueMap[typeof key];
              case UIKey.IS_ORG_LOADING:
                return z.boolean().safeParse(uiState.value).data as UIValueMap[typeof key];
              case UIKey.SESSION_INITIALIZED:
                return z.boolean().safeParse(uiState.value).data as UIValueMap[typeof key];
              case UIKey.NOTIFICATION_DISMISSED:
                return z.boolean().safeParse(uiState.value).data as UIValueMap[typeof key];
              case UIKey.IS_TOP_BANNER_ACTIVE:
                return z.boolean().safeParse(uiState.value).data as UIValueMap[typeof key];
            }
          },

          clearUtmValues: function () {
            // Clear UTM values from store (they will also be cleared from sessionStorage by persist middleware)
            for (const utmKey of UTM_KEYS) {
              get().removeFromStore(utmKey);
            }
          },
        };
        return store;
      },
      {
        name: 'blym-ui-key-value-utm',
        storage: createJSONStorage(() => sessionStorage),
        // Only persist UTM-related keys from the record
        partialize: function (state) {
          const utmRecords: Record<string, { data: TModel; isLoading: boolean; hasError: any }> =
            {};
          for (const utmKey of UTM_KEYS) {
            const record = state.record[utmKey];
            if (record?.data != null) {
              utmRecords[utmKey] = {
                data: record.data,
                isLoading: false,
                hasError: undefined,
              };
            }
          }
          // Return only the record with UTM keys (serializable data only)
          return { record: utmRecords };
        },
        // Merge persisted UTM values back into the store
        merge: function (persistedState, currentState) {
          if (persistedState == null || typeof persistedState !== 'object') {
            return currentState;
          }

          const persisted = persistedState as {
            record?: Record<string, { data: TModel; isLoading: boolean; hasError: any }>;
          };
          if (persisted.record == null || Object.keys(persisted.record).length === 0) {
            return currentState;
          }

          // Merge persisted UTM records into current state
          // Always prefer persisted values for UTM keys to ensure they survive page refreshes
          const mergedRecord = { ...currentState.record };
          for (const [key, persistedRecord] of Object.entries(persisted.record)) {
            if (
              UTM_KEYS.includes(key as (typeof UTM_KEYS)[number]) &&
              persistedRecord?.data != null
            ) {
              const currentRecord = mergedRecord[key];
              // Use persisted value if current doesn't exist, or if persisted is newer or equal
              if (
                currentRecord?.data == null ||
                persistedRecord.data.updatedAt >= (currentRecord.data.updatedAt ?? 0)
              ) {
                mergedRecord[key] = persistedRecord;
              }
            }
          }

          return {
            ...currentState,
            record: mergedRecord,
          };
        },
        // Skip hydration errors
        skipHydration: false,
      }
    ),
    { name: 'blym', store: 'ui-key-value' }
  )
);
